import { useEffect, useState } from "react";
import { formatMoneyFull } from "@/lib/money";
import { saveStatusRing, type CellStatus } from "../utils/useSaveStatus";

interface Props {
  initial: number | null;
  currency?: string | null;
  status?: CellStatus;
  disabled?: boolean;
  onCommit: (value: number | null) => void;
}

// Strip everything except digits and a decimal point, collapsing any digits
// after a second "." so the user can never type more than one.
function sanitizeDecimalInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
}

// One editable price cell on the pricings table. An emptied cell commits
// null (clears the stored price) instead of zero.
// Shows "4,300,000" while idle; raw digits while the cell is focused.
export default function PriceCellInput({ initial, currency, status, disabled, onCommit }: Props) {
  const [value, setValue] = useState(initial === null ? "" : String(initial));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setValue(initial === null ? "" : String(initial));
  }, [initial]);

  const display = focused || value === "" ? value : formatMoneyFull(value, currency);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      disabled={disabled}
      onChange={(e) => setValue(sanitizeDecimalInput(e.currentTarget.value))}
      onFocus={() => setFocused(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseFloat(value);
        const next = Number.isNaN(parsed) ? null : parsed;
        if (next !== initial || status === "error") onCommit(next);
      }}
      style={{
        background: "var(--background)",
        borderColor: "var(--border)",
        color: "var(--foreground)",
        boxShadow: saveStatusRing(status),
      }}
      className="w-full text-right text-xs border rounded-md pl-4 pr-1.5 py-1 outline-none transition-colors focus:border-secondary placeholder:text-muted-foreground/50"
    />
  );
}
